import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";
import { SoundControls } from "@/components/SoundControls";
import { ShareButton } from "@/components/ShareButton";

const CakeBake = lazy(() => import("@/components/games/CakeBake").then((m) => ({ default: m.CakeBake })));

const TITLE = "Palworld Cake Baking Game — Free Simulator";
const DESCRIPTION =
  "Free Palworld cake baking simulator with Sekhmet: gather ingredients, stir the batter and time the oven for perfect cakes. Animated, no download needed.";
const OG_IMAGE = "https://palstars.com/og/cake-bake.jpg";

const STEPS = [
  { name: "Pick Normal or Hard", text: "Normal bakes with three ingredients per cake and a wide oven window; Hard uses four ingredients, a narrow window and a faster needle." },
  { name: "Gather the ingredients", text: "Sekhmet calls out the cake recipe — tap only the listed ingredients, because a wrong ingredient costs you a second." },
  { name: "Stir the batter", text: "Tap the stir button until the mixing bar fills; the batter animation speeds up as the bowl comes together." },
  { name: "Time the oven", text: "Stop the sweeping oven needle inside the green band for a perfect bake, close for a good cake, wide for burnt edges." },
  { name: "Share the shift", text: "At the end of the shift copy your cake count, points and perfect bakes with a share link back to the game." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Palworld cake baking game?", a: "Sekhmet's Cake Kitchen is a free animated Palworld cake baking simulator on Pal Stars: you gather the cake ingredients Sekhmet asks for, stir the batter and stop the oven needle in the green band to bake a perfect cake before the timer runs out." },
  { q: "Who is the chef in the cake baking simulator?", a: "Sekhmet — the Ground-element Pal from the official Paldeck — runs the kitchen, idling with a bob animation and leaning into the bowl while you stir." },
  { q: "Is the Palworld cake game free?", a: "Yes. It runs entirely in your browser with no download and no account, and it is cosmetic: it never pays booster packs, Palsino spins or gold." },
  { q: "What is the difference between Normal and Hard baking?", a: "Normal runs 60 seconds with three ingredients per cake and a wide perfect window. Hard runs 55 seconds with four ingredients, a much narrower window and a faster oven needle, but perfect bakes are worth double points." },
  { q: "What are cakes used for in Palworld?", a: "In the game a cake is the item the Breeding Farm consumes to hatch an egg, baked from flour, milk, eggs, honey and berries at a Cooking Pot. The simulator uses the same ingredient list." },
  { q: "How many baking shifts can I play per day?", a: "Twenty-five shifts per browser each UTC day, which keeps score spam down while leaving plenty of practice." },
  { q: "Does the cake baking game work on mobile?", a: "Yes. Every control is a large tap target, the kitchen stacks into one column on a phone, and the sitewide volume control applies here too." },
];

export const Route = createFileRoute("/cake-bake")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld Cake Baking Simulator — bake with Sekhmet" },
      { property: "og:description", content: "Gather ingredients, stir the batter and time the oven. Free animated Palworld cake baking game." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/cake-bake" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: "Pal Stars Arcade — free Palworld browser games" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/cake-bake" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Cake Baking Simulator", "/cake-bake"]]),
      toolSchema({
        name: "Palworld Cake Baking Simulator",
        description: DESCRIPTION,
        path: "/cake-bake",
      }),
      howToSchema({
        name: "How to play the Palworld cake baking simulator",
        description: "Pick a difficulty, gather Sekhmet's cake ingredients, stir the batter and stop the oven needle in the green band.",
        path: "/cake-bake",
        steps: STEPS,
      }),
      faqSchema(FAQ, "https://palstars.com/cake-bake"),
    ],
  }),
  component: CakeBakePage,
});

function CakeBakePage() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade</p>
        <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld cake baking simulator with Sekhmet</h1>
          <ShareButton title={"Palworld cake baking simulator with Sekhmet — Pal Stars"} text={"Palworld cake baking simulator on Pal Stars."} path="/cake-bake" />
        </div>
        <p className="aeo-answer mt-3 text-sm text-muted-foreground">
          Sekhmet's Cake Kitchen is a free, animated Palworld cake baking game: gather the exact cake ingredients, stir the
          batter and stop the oven needle inside the green band. It plays in one screen on desktop and mobile, needs no
          download or account, and is purely cosmetic — no packs, spins or gold.
        </p>
      </header>

      <SoundControls />

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Palworld cake baking simulator</h2><p className="mt-2 text-sm text-muted-foreground">Heating the oven…</p></section>}>
        <CakeBake />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How to play</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Cakes, breeding and more games</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/guides/$slug" params={{ slug: "palworld-cake-guide" }} className="font-semibold text-primary hover:underline">Palworld cake guide →</Link> the real recipe and farm setup.</li>
          <li><Link to="/breeding" className="font-semibold text-primary hover:underline">Palworld breeding calculator →</Link> what to hatch once the cake is baked.</li>
          <li><Link to="/build-sim" className="font-semibold text-primary hover:underline">Base building simulator →</Link> build the kitchen with Cattiva.</li>
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link> the full arcade.</li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld cake baking game FAQ" />
    </div>
  );
}
